import { authAPI } from './api.js';

export const isPortalFlowId = (value) =>
    typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const STORAGE_KEY = 'elevate.portalLogin';

export const savePortalAttempt = (attempt) => {
    try {
        if (attempt) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempt));
        else window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // Sign-in still works when storage is unavailable; only recovery after
        // reloading the portal will require starting another attempt.
    }
};

export const restorePortalAttempt = ({ locationId, userId }) => {
    try {
        const attempt = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));
        if (
            isPortalFlowId(attempt?.flowId)
            && attempt.locationId === locationId
            && attempt.userId === userId
            && Number.isFinite(attempt.expiresAt)
            && attempt.expiresAt > Date.now()
            && attempt.expiresAt <= Date.now() + 10 * 60 * 1000
        ) return attempt;
    } catch {
        // Ignore invalid, expired, or inaccessible notification state.
    }
    savePortalAttempt(null);
    return null;
};

export const portalDashboardPath = ({ locationId, userId, flowId, integration }) => {
    const params = new URLSearchParams({ location: locationId, user: userId });
    if (integration) params.set('integration', integration);
    if (flowId) params.set('portalFlow', flowId);
    return `/dashboard?${params}`;
};

const waitForAbortableResult = (promise, signal) => new Promise((resolve, reject) => {
    const abort = () => {
        signal.removeEventListener('abort', abort);
        reject(new DOMException('Portal request aborted', 'AbortError'));
    };
    Promise.resolve(promise).then(
        value => {
            signal.removeEventListener('abort', abort);
            resolve(value);
        },
        error => {
            signal.removeEventListener('abort', abort);
            reject(error);
        }
    );
    if (signal.aborted) abort();
    else signal.addEventListener('abort', abort, { once: true });
});

// Browser timers can pause while Google is in the foreground. Wake promptly
// when the portal is visible again, but never run two active probes together.
export const watchPortalRequest = ({
    request,
    onResult,
    onError,
    onTimeout = () => {},
    timeoutMs = 10 * 60 * 1000
}) => {
    let stopped = false;
    let nextPoll;
    let activeRequest = null;
    let requestExpiresAt = 0;
    let wakePending = false;
    const duration = Number.isFinite(timeoutMs) ? Math.max(0, timeoutMs) : 0;
    const expiresAt = Date.now() + duration;
    const stop = () => {
        if (stopped) return;
        stopped = true;
        clearTimeout(nextPoll);
        clearTimeout(deadline);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('focus', wake);
        window.removeEventListener('online', wake);
        activeRequest?.abort();
    };
    const expire = () => {
        if (stopped) return;
        stop();
        onTimeout();
    };
    const deadline = setTimeout(expire, duration);

    function wake() {
        if (stopped) return;
        if (Date.now() >= expiresAt) {
            expire();
            return;
        }
        if (activeRequest) {
            // A suspended tab may not have run the request timeout yet. Abort
            // the stale probe and let its finally block start one fresh probe.
            if (Date.now() >= requestExpiresAt) {
                wakePending = true;
                activeRequest.abort();
            }
            return;
        }
        clearTimeout(nextPoll);
        nextPoll = undefined;
        poll();
    }

    function onVisibilityChange() {
        if (document.visibilityState === 'visible') wake();
    }

    const poll = async () => {
        if (stopped || activeRequest) return;
        if (Date.now() >= expiresAt) {
            expire();
            return;
        }
        const controller = new AbortController();
        activeRequest = controller;
        requestExpiresAt = Date.now() + 8000;
        const abortRequest = () => controller.abort();
        const requestDeadline = setTimeout(abortRequest, 8000);
        const isCurrent = () => {
            if (stopped || controller.signal.aborted) return false;
            if (Date.now() >= expiresAt) {
                expire();
                return false;
            }
            if (Date.now() >= requestExpiresAt) {
                controller.abort();
                return false;
            }
            return true;
        };
        try {
            const result = await waitForAbortableResult(request(controller.signal), controller.signal);
            if (!isCurrent()) return;
            const finished = await waitForAbortableResult(
                onResult(result, controller.signal),
                controller.signal
            );
            if (!isCurrent()) return;
            if (finished === true) {
                stop();
            }
        } catch (error) {
            if (!stopped && !controller.signal.aborted && onError?.(error) === true) stop();
        } finally {
            clearTimeout(requestDeadline);
            activeRequest = null;
            if (!stopped) {
                const delay = wakePending ? 0 : 1500;
                wakePending = false;
                nextPoll = setTimeout(poll, delay);
            }
        }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', wake);
    window.addEventListener('online', wake);
    poll();
    return stop;
};

// This channel only reports progress. Authentication still requires the
// HttpOnly session and an exact tenant/user check in the embedded dashboard.
export const watchPortalLogin = ({ flowId, onStatus, onTimeout, timeoutMs }) =>
    watchPortalRequest({
        request: signal => authAPI.getPortalLoginStatus(flowId, { signal, cache: 'no-store' }),
        onResult: (result, signal) => result.success && onStatus(result, signal),
        onTimeout,
        timeoutMs
    });
